
interface Product {
  id: number;
  title: string;
  category: string;
  brand: string;
  model: string;
  certification: string;
  status: string;
  image: string;
  description: string;
}

interface ProductDetailHeroProps {
  product: Product;
}

export default function ProductDetailHero({ product }: ProductDetailHeroProps) {
  return (
    <section className="bg-gradient-to-br from-gray-50 to-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <a href="/" className="hover:text-red-600 transition-colors">Home</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/products" className="hover:text-red-600 transition-colors">Products</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-900">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-square bg-white rounded-2xl shadow-xl overflow-hidden">
              <img
                src={product.image}
                alt={product.title}
                className="w-full h-full object-cover object-center"
              />
            </div>
            
            {/* Status Badge */}
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <i className="ri-check-line mr-1"></i>
                {product.status}
              </span>
            </div>

            {/* Share Button */}
            <div className="absolute top-4 right-4">
              <button className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors">
                <i className="ri-share-line text-gray-600"></i>
              </button>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                  {product.category}
                </span>
                <span className="text-sm text-gray-500">Model: {product.model}</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {product.title}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Key Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Brand</div>
                <div className="font-semibold text-gray-900">{product.brand}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Certification</div>
                <div className="font-semibold text-gray-900">{product.certification}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button className="flex-1 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                <i className="ri-mail-line mr-2"></i>
                Request Quote
              </button>
              <button className="flex-1 border-2 border-gray-300 hover:border-red-600 text-gray-700 hover:text-red-600 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Datasheet
              </button>
            </div>

            {/* Quick Contact */}
            <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="text-sm text-blue-600 font-medium">Need immediate assistance?</div>
                <div className="text-sm text-blue-800">Call our technical team</div>
              </div>
              <a 
                href="tel:+97455544093"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
